import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Company} from '../_models/company';
import {EffectiveRole, User} from '../_models/user';
import {TargetCompanyService} from './target-company.service';

export interface WorkspaceDestination {
  external: boolean;
  url: string;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyWorkspaceService {
  private readonly preferredCompanyKey = 'colorjob_preferred_company_id';
  private readonly autoOpenCompanyKey = 'colorjob_auto_open_company';
  private readonly mainDomain = 'colorjob.com';

  constructor(
    private router: Router,
    private targetCompanyService: TargetCompanyService
  ) {
  }

  getPreferredCompanyId(): number | null {
    if (!this.isAutoOpenEnabled()) {
      return null;
    }

    const storedValue = localStorage.getItem(this.preferredCompanyKey);
    const parsedValue = storedValue ? Number(storedValue) : null;
    return parsedValue && Number.isFinite(parsedValue) ? parsedValue : null;
  }

  savePreferredCompany(companyId: number): void {
    localStorage.setItem(this.autoOpenCompanyKey, 'true');
    localStorage.setItem(this.preferredCompanyKey, String(companyId));
  }

  rememberLastCompany(companyId: number): void {
    localStorage.setItem(this.preferredCompanyKey, String(companyId));
  }

  clearPreferredCompany(): void {
    localStorage.removeItem(this.autoOpenCompanyKey);
    localStorage.removeItem(this.preferredCompanyKey);
  }

  resolvePreferredCompany(user: User | null | undefined): Company | null {
    const preferredCompanyId = this.getPreferredCompanyId();
    if (!preferredCompanyId || !user?.companies?.length) {
      return null;
    }

    const preferredCompany = user.companies.find((company) => company.id === preferredCompanyId) ?? null;
    if (!preferredCompany) {
      this.clearPreferredCompany();
      return null;
    }

    return preferredCompany;
  }

  getHostMatchedCompany(user: User | null | undefined): Company | null {
    const currentSubdomain = this.getCurrentSubdomain();
    if (!currentSubdomain || currentSubdomain === 'app') {
      return null;
    }

    return user?.companies?.find((company) => company.subdomain?.toLowerCase() === currentSubdomain) ?? null;
  }

  shouldShowWelcomeScreen(user: User | null | undefined): boolean {
    if (!user || user.effectiveRole === EffectiveRole.ROLE_SUPER_ADMIN) {
      return false;
    }

    return !this.resolvePreferredCompany(user);
  }

  resolvePostLoginDestination(user: User | null | undefined): WorkspaceDestination {
    if (!user) {
      return {external: false, url: '/login'};
    }

    if (user.effectiveRole === EffectiveRole.ROLE_SUPER_ADMIN) {
      return {external: false, url: '/dashboard'};
    }

    const companies = user.companies ?? [];
    const hostMatchedCompany = this.getHostMatchedCompany(user);
    if (hostMatchedCompany) {
      this.targetCompanyService.setTargetCompanyId(hostMatchedCompany.id);
      return this.getCompanyDestination(hostMatchedCompany, true, user);
    }

    const preferredCompany = this.resolvePreferredCompany(user);
    if (preferredCompany) {
      this.targetCompanyService.setTargetCompanyId(preferredCompany.id);
      return this.getCompanyDestination(preferredCompany, false, user);
    }

    if (!companies.length) {
      this.targetCompanyService.clearTargetCompanyId();
      return {external: false, url: '/welcome'};
    }

    this.targetCompanyService.clearTargetCompanyId();
    return {external: false, url: '/welcome'};
  }

  getCompanyDestination(
    company: Company,
    forceInternalNavigation = false,
    user?: User | null
  ): WorkspaceDestination {
    if (!company?.id) {
      return {external: false, url: '/login'};
    }

    const internalCompanyUrl = this.getCompanyHomeUrl(company.id, user?.effectiveRole);

    const currentSubdomain = this.getCurrentSubdomain();
    if (forceInternalNavigation || (currentSubdomain && currentSubdomain === company.subdomain?.toLowerCase())) {
      return {
        external: false,
        url: internalCompanyUrl
      };
    }

    const externalUrl = this.getExternalCompanyUrl(company, internalCompanyUrl);
    if (externalUrl) {
      return {
        external: true,
        url: externalUrl
      };
    }

    return {
      external: false,
      url: internalCompanyUrl
    };
  }

  getCompanyHomeUrl(companyId: number, effectiveRole?: EffectiveRole | null): string {
    if (effectiveRole === EffectiveRole.ROLE_USER) {
      return `/company/${companyId}/projects`;
    }

    return `/company/${companyId}/dashboard`;
  }

  navigateToDestination(destination: WorkspaceDestination): void {
    if (destination.external) {
      window.location.assign(destination.url);
      return;
    }

    this.router.navigateByUrl(destination.url);
  }

  isAppWorkspaceHost(): boolean {
    const hostname = window.location.hostname.toLowerCase();

    if (!hostname.endsWith(this.mainDomain)) {
      return true;
    }

    const subdomain = this.getCurrentSubdomain();
    return !subdomain || subdomain === 'app';
  }

  private isAutoOpenEnabled(): boolean {
    return localStorage.getItem(this.autoOpenCompanyKey) === 'true';
  }

  getAllCompaniesDestination(): WorkspaceDestination {
    const externalUrl = this.getAppWorkspaceUrl('/welcome');

    if (externalUrl) {
      return {
        external: true,
        url: externalUrl
      };
    }

    return {
      external: false,
      url: '/welcome'
    };
  }

  getCompanySwitcherDestination(company: Company): WorkspaceDestination {
    if (!company?.id) {
      return {external: false, url: '/login'};
    }

    const currentSubdomain = this.getCurrentSubdomain();
    if (currentSubdomain && currentSubdomain === company.subdomain?.toLowerCase()) {
      return {
        external: false,
        url: `/company/${company.id}/dashboard`
      };
    }

    const externalUrl = this.getExternalCompanyUrl(company, `/company/${company.id}/dashboard`);
    if (externalUrl) {
      return {
        external: true,
        url: externalUrl
      };
    }

    return {
      external: false,
      url: `/company/${company.id}/dashboard`
    };
  }

  private getExternalCompanyUrl(company: Company, pathname = ''): string | null {
    if (!company?.subdomain || !this.canUseExternalCompanyHost()) {
      return null;
    }

    const normalizedPath = pathname.startsWith('/') || !pathname
      ? pathname
      : `/${pathname}`;

    return `${window.location.protocol}//${company.subdomain}.colorjob.com${normalizedPath}`;
  }

  private getAppWorkspaceUrl(pathname = ''): string | null {
    if (!this.canUseExternalCompanyHost()) {
      return null;
    }

    const normalizedPath = pathname.startsWith('/') || !pathname
      ? pathname
      : `/${pathname}`;

    return `${window.location.protocol}//app.${this.mainDomain}${normalizedPath}`;
  }

  private canUseExternalCompanyHost(): boolean {
    const hostname = window.location.hostname.toLowerCase();
    return hostname === this.mainDomain || hostname.endsWith(`.${this.mainDomain}`);
  }

  private getCurrentSubdomain(): string | null {
    const hostname = window.location.hostname.toLowerCase();

    if (hostname === this.mainDomain || !hostname.endsWith(`.${this.mainDomain}`)) {
      return null;
    }

    const subdomain = hostname.substring(0, hostname.length - this.mainDomain.length - 1);
    return subdomain && !subdomain.includes('.') ? subdomain : null;
  }
}
